import { z } from 'zod';
import { ReactElement, useMemo, useContext, useState, useCallback } from 'react';
import { DrawerForm, ErrorResponse, SuccessNotificationContext } from '@dmc/ui';
import { useTranslation } from 'react-i18next';
import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAppDispatch } from '@casops/store/hooks';
import { closeSidePanel } from '@casops/store/slices/globalSlice';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ApiBaseQueryError, ModelName } from '@casops/store/api';
import { VdsButton } from '@dmc/ui/vds';
import ErrorMessage from '@cas/components/state-management/error-message/ErrorMessage';
import { useForm } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';
import CasFormDropdown from '@cas/components/inputs/cas-form-dropdown/CasFormDropdown';
import {
  GeofencePinDropModel,
  useEditGeofencePinDropMutation,
} from '@casops/store/apis/geofencePinDropApi';
import { FenceType } from '@casops/store/apis/geofencesApi';
import useStyleOptions from '@casops/side-panels/manage-geofence/components/general/useStyleOptions';
import { useLegacyFenceTypesOptions } from './useLegacyFenceTypeOptions';

type FormInputModel = GeofencePinDropModel;

const defaultSettings: FormInputModel = {
  ft: FenceType.Standard,
  stid: -1, // Default style id.
  rad: 1,
};

interface PinDropTemplateFormProps {
  settings?: GeofencePinDropModel;
}

export const PinDropTemplateForm = ({ settings }: PinDropTemplateFormProps): ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { showSuccessMessage } = useContext(SuccessNotificationContext);
  const [mutationTrigger] = useEditGeofencePinDropMutation();

  const initialValues = useMemo<FormInputModel>(() => {
    return settings != null ? settings : defaultSettings;
  }, [settings]);

  const schema = useMemo(() => {
    return z.object({
      ft: z.nativeEnum(FenceType, { message: t('Validation.SpecifyFenceType') }),
      stid: z.number().min(-1, { message: t('Validation.SpecifyStyleName') }),
      rad: z.number().positive(),
    });
  }, [t]);

  type SchemaOutput = z.infer<typeof schema>;

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, dirtyFields, isValid },
  } = useForm<FormInputModel, undefined, SchemaOutput>({
    defaultValues: initialValues,
    resolver: zodResolver(schema),
    mode: 'all',
    reValidateMode: 'onBlur',
  });

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  // Don't use the isDirty flag with ReactHookForm - see https://github.com/react-hook-form/react-hook-form/issues/3213.
  const isAnyFieldDirty = Object.keys(dirtyFields).length > 0;
  const canSubmit = !isSubmitting && isValid && isAnyFieldDirty;

  const onSubmitInternal = useCallback(
    async (data: SchemaOutput) => {
      try {
        setErrorMessage(undefined);

        await mutationTrigger(data).unwrap();

        // If we got here, it's successful because the methods above would have thrown
        showSuccessMessage(t(`Model.Edit.Success`, { modelName: ModelName.GeofencePinDrop }));

        dispatch(closeSidePanel());
      } catch (e: ApiBaseQueryError) {
        const errorResponse = new ErrorResponse(e);
        setErrorMessage(t('Failed!') + ' ' + errorResponse.getTitle());
      }
    },
    [t, dispatch, setErrorMessage, mutationTrigger, showSuccessMessage],
  );

  const onSubmit = useCallback((): void => {
    handleSubmit(onSubmitInternal)();
  }, [handleSubmit, onSubmitInternal]);

  const styleOptions = useStyleOptions();
  const fencetypeOptions = useLegacyFenceTypesOptions();

  return (
    <>
      <DrawerForm
        footer={
          <VdsButton
            variant="primary"
            color="secondary-blue"
            isFullWidth
            disabled={!canSubmit}
            onClick={onSubmit}
          >
            {t('SaveChanges')}
          </VdsButton>
        }
      >
        <>
          {errorMessage !== undefined && <ErrorMessage message={errorMessage} />}

          <CasFormDropdown
            control={control}
            name="ft"
            label={t('Fields.GeofenceType')}
            items={fencetypeOptions}
          />
          <CasFormDropdown
            control={control}
            name="stid"
            label={t('Fields.StyleName')}
            items={styleOptions}
          />
          <CasFormInput control={control} name="rad" label={t('Fields.Radius')} type="number" />
        </>
      </DrawerForm>
      <DevTool control={control} styles={{ panel: { height: '90vh' } }} placement="bottom-left" />
    </>
  );
};

export default PinDropTemplateForm;
